import { DatePipe, TitleCasePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { lastValueFrom } from 'rxjs';
import {
  SkeeloPersonBenefitReportDTO,
  SubscriberInfo
} from 'src/app/admin-api';
import { SubscriberControllerService } from 'src/app/admin-api/api/subscriberController.service';
import { TableColumn } from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

interface SkeeloDashboardMetricsDTO {
  cancelledCount?: number;
  cancelledThisMonthCount?: number;
  cancelledThisWeekCount?: number;
  cancelledTodayCount?: number;
  thisMonthCount?: number;
  thisWeekCount?: number;
  todayCount?: number;
  totalActiveCount?: number;
}

interface SkeeloChartDataDTO {
  benefitCount?: number;
  cancelledCount?: number;
  date?: string;
}

interface SkeeloPlanSummaryDTO {
  planCode?: string;
  total?: number;
}

@Component({
  selector: 'app-skeelo-dashboard',
  templateUrl: './skeelo-dashboard.component.html',
  styleUrls: ['./skeelo-dashboard.component.scss'],
  providers: [DatePipe, TitleCasePipe]
})
export class SkeeloDashboardComponent implements OnInit {
  private title = inject(Title);
  private subscriberService = inject(SubscriberControllerService);
  private datePipe = inject(DatePipe);
  private titleCasePipe = inject(TitleCasePipe);

  selectedMonth = this.datePipe.transform(new Date(), 'yyyyMM') || '';
  months: Array<{ label: string; value: string }> = [];

  metrics: SkeeloDashboardMetricsDTO = {};
  chartRawData: Array<SkeeloChartDataDTO> = [];
  planSummaryData: Array<SkeeloPlanSummaryDTO> = [];
  subscribers: Array<SubscriberInfo> = [];
  selectedSubscriber: SubscriberInfo | null = null;
  personBenefitsData: Array<SkeeloPersonBenefitReportDTO> = [];
  subscriberSearched = false;

  subscriberFilters: {
    personId: number | null;
    subscriberId: number | null;
    email: string;
    boxId: string;
  } = {
    personId: null,
    subscriberId: null,
    email: '',
    boxId: ''
  };

  chartData: any;
  chartOptions: any;

  chartDetailsCols = [
    new TableColumn.Builder()
      .setHeader('Plano Skeelo')
      .setField('planCode')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Total')
      .setField('total')
      .setType('formattedInteger')
      .setCondition('gte')
      .build()
  ];

  personBenefitsCols = [
    new TableColumn.Builder()
      .setHeader('Edição')
      .setField('editionId')
      .setCondition('equals')
      .setType('number')
      .build(),
    new TableColumn.Builder()
      .setHeader('Plano Skeelo')
      .setField('planCode')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Assinatura')
      .setField('subscriptionName')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Data benefício')
      .setField('dateCreated')
      .setType('date')
      .setCondition('between')
      .setDisplayFunction((item: SkeeloPersonBenefitReportDTO) =>
        item?.dateCreated
          ? this.datePipe.transform(item.dateCreated, 'dd/MM/yyyy HH:mm')
          : '-'
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Data cancelamento')
      .setField('dateDeactivated')
      .setType('date')
      .setCondition('between')
      .setDisplayFunction((item: SkeeloPersonBenefitReportDTO) =>
        item?.dateDeactivated
          ? this.datePipe.transform(item.dateDeactivated, 'dd/MM/yyyy HH:mm')
          : '-'
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Status')
      .setField('active')
      .setType('status')
      .setCondition('equals')
      .setStatusFunction((item: SkeeloPersonBenefitReportDTO) => {
        if (item?.active === true) {
          return 1;
        }

        if (item?.active === false) {
          return -1;
        }

        return 0;
      })
      .setDisplayFunction((item: SkeeloPersonBenefitReportDTO) =>
        item?.active === true
          ? 'Ativo'
          : item?.active === false
            ? 'Cancelado'
            : 'Pendente'
      )
      .build()
  ];

  readonly metricCards = [
    { key: 'todayCount', label: 'Benefícios hoje' },
    { key: 'thisWeekCount', label: 'Benefícios na semana atual' },
    { key: 'thisMonthCount', label: 'Benefícios no mês  atual' },
    { key: 'totalActiveCount', label: 'Total ativos na base' },
    { key: 'cancelledTodayCount', label: 'Cancelados hoje' },
    { key: 'cancelledThisWeekCount', label: 'Cancelados na semana atual' },
    { key: 'cancelledThisMonthCount', label: 'Cancelados no mês atual' },
    { key: 'cancelledCount', label: 'Total cancelados na base' }
  ] as const;

  async ngOnInit(): Promise<void> {
    this.title.setTitle('Integrações - Skeelo');
    this.initializeMonths();
    await this.loadData();
  }

  async onMonthChange(): Promise<void> {
    if (!this.selectedMonth) {
      return;
    }

    try {
      LoaderService.showLoader();
      await this.loadChartData();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async searchSubscribers(): Promise<void> {
    const personId = this.subscriberFilters.personId || undefined;
    const subscriberId = this.subscriberFilters.subscriberId || undefined;
    const email = this.subscriberFilters.email?.trim() || undefined;
    const boxId = this.subscriberFilters.boxId?.trim() || undefined;

    if (!personId && !subscriberId && !email && !boxId) {
      return;
    }

    try {
      LoaderService.showLoader();
      const response = await lastValueFrom(
        this.subscriberService.search(personId, subscriberId, email, boxId)
      );

      this.subscribers = (response?.result || []) as Array<SubscriberInfo>;
      this.subscriberSearched = true;
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  selectSubscriber(subscriber: SubscriberInfo): void {
    this.selectedSubscriber = subscriber;
    this.subscriberFilters.personId = subscriber.personId || null;
    this.loadPersonBenefits();
  }

  clearSelectedSubscriber(): void {
    this.selectedSubscriber = null;
    this.personBenefitsData = [];
  }

  get canSearchSubscribers(): boolean {
    return Boolean(
      this.subscriberFilters.personId ||
        this.subscriberFilters.subscriberId ||
        this.subscriberFilters.email?.trim() ||
        this.subscriberFilters.boxId?.trim()
    );
  }

  get hasPersonBenefits(): boolean {
    return this.personBenefitsData.length > 0;
  }

  private async loadPersonBenefits(): Promise<void> {
    if (!this.selectedSubscriber?.personId) {
      this.personBenefitsData = [];
      return;
    }

    try {
      LoaderService.showLoader();
      const response = await lastValueFrom(
        this.subscriberService.getSkeeloPersonBenefitsReport(
          this.selectedSubscriber.personId
        )
      );

      this.personBenefitsData = (response?.result ||
        []) as Array<SkeeloPersonBenefitReportDTO>;
    } catch (error) {
      AppDialogService.showErrorDialog(error);
      this.personBenefitsData = [];
    } finally {
      LoaderService.showLoader(false);
    }
  }

  getMetricValue(metricKey: keyof SkeeloDashboardMetricsDTO): number {
    return Number(this.metrics?.[metricKey] || 0);
  }

  get selectedMonthName(): string {
    return (
      this.months.find((month) => month.value === this.selectedMonth)?.label ||
      ''
    );
  }

  get chartDetails(): Array<SkeeloPlanSummaryDTO> {
    return [...this.planSummaryData].sort((a, b) => {
      const aPlan = (a.planCode || '').toLowerCase();
      const bPlan = (b.planCode || '').toLowerCase();
      return aPlan.localeCompare(bPlan);
    });
  }

  async loadData(): Promise<void> {
    try {
      LoaderService.showLoader();

      const [metrics, chartData, planSummary] = await Promise.all([
        lastValueFrom(this.subscriberService.getSkeeloDashboard()),
        lastValueFrom(
          this.subscriberService.getSkeeloChartData(Number(this.selectedMonth))
        ),
        lastValueFrom(
          this.subscriberService.getSkeeloPlanSummary(
            Number(this.selectedMonth)
          )
        )
      ]);

      this.metrics = (metrics?.result || {}) as SkeeloDashboardMetricsDTO;
      this.chartRawData = ((chartData?.result || []) as Array<any>).map(
        (row: any) => ({
          benefitCount: Number(row.benefitCount || 0),
          cancelledCount: Number(row.cancelledCount || 0),
          date: row.date
        })
      );
      this.planSummaryData = ((planSummary?.result || []) as Array<any>).map(
        (row: any) => ({
          planCode: row.planCode,
          total: Number(row.total || 0)
        })
      );

      this.buildChartData();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private async loadChartData(): Promise<void> {
    const [chartData, planSummary] = await Promise.all([
      lastValueFrom(
        this.subscriberService.getSkeeloChartData(Number(this.selectedMonth))
      ),
      lastValueFrom(
        this.subscriberService.getSkeeloPlanSummary(Number(this.selectedMonth))
      )
    ]);

    this.chartRawData = ((chartData?.result || []) as Array<any>).map(
      (row: any) => ({
        benefitCount: Number(row.benefitCount || 0),
        cancelledCount: Number(row.cancelledCount || 0),
        date: row.date
      })
    );
    this.planSummaryData = ((planSummary?.result || []) as Array<any>).map(
      (row: any) => ({
        planCode: row.planCode,
        total: Number(row.total || 0)
      })
    );
    this.buildChartData();
  }

  private initializeMonths(): void {
    this.months = [];

    let date = new Date(new Date().setDate(5));
    while (date.getFullYear() >= 2025) {
      this.months.push({
        label: this.titleCasePipe.transform(
          this.datePipe.transform(date, 'MMMM yyyy', undefined, 'pt-BR') || ''
        ),
        value: this.datePipe.transform(date, 'yyyyMM') || ''
      });
      date = new Date(date.setMonth(date.getMonth() - 1));
    }
  }

  private buildChartData(): void {
    const groupedByDate = new Map<
      string,
      { benefitCount: number; cancelledCount: number }
    >();

    this.chartRawData.forEach((item) => {
      const date = this.toDateOnlyLabel(item.date);
      const current = groupedByDate.get(date) || {
        benefitCount: 0,
        cancelledCount: 0
      };

      groupedByDate.set(date, {
        benefitCount: current.benefitCount + Number(item.benefitCount || 0),
        cancelledCount:
          current.cancelledCount + Number(item.cancelledCount || 0)
      });
    });

    const labels = Array.from(groupedByDate.keys()).sort(
      (a, b) =>
        this.parseDateLabel(a).getTime() - this.parseDateLabel(b).getTime()
    );

    const benefitSeries = labels.map(
      (date) => groupedByDate.get(date)?.benefitCount || 0
    );

    const cancelledSeries = labels.map(
      (date) => groupedByDate.get(date)?.cancelledCount || 0
    );

    this.chartData = {
      labels,
      datasets: [
        {
          label: 'Quantidade de benefícios',
          data: benefitSeries,
          backgroundColor: '#2563eb'
        },
        {
          label: 'Quantidade de benefícios cancelados',
          data: cancelledSeries,
          backgroundColor: '#ef4444'
        }
      ]
    };

    this.chartOptions = {
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: {
          labels: {
            color: '#1f2937'
          }
        },
        tooltip: {
          callbacks: {
            title: (context: Array<{ label?: string }>) =>
              `Data: ${context?.[0]?.label || '-'}`
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: '#4b5563'
          },
          grid: {
            color: '#e5e7eb'
          }
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: '#4b5563'
          },
          grid: {
            color: '#e5e7eb'
          }
        }
      }
    };
  }

  private toDateOnlyLabel(date?: string): string {
    if (!date) {
      return '-';
    }

    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) {
      return date;
    }

    const day = parsed.getDate().toString().padStart(2, '0');
    const month = (parsed.getMonth() + 1).toString().padStart(2, '0');
    const year = parsed.getFullYear();

    return `${day}/${month}/${year}`;
  }

  private parseDateLabel(label: string): Date {
    const parts = label.split('/');
    if (parts.length !== 3) {
      return new Date(0);
    }

    const [day, month, year] = parts.map((part) => Number(part));
    return new Date(year, month - 1, day);
  }
}
